import { t } from "i18next"
import { useEffect, useRef, useContext, useCallback } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import { ConsoleLogger } from "src/util/logger"
import { useIsMobileApp } from "src/util/deviceType"
import { AuthedUser } from "src/recoil/atoms/authedUser"
import {
  chatIsReadSelector,
  currentChatGenerationAtom,
  recentUploadAttachmentUrlAtom
} from "src/recoil/atoms/chats"
import {
  TrulienceStateCallbackSelector,
  TrulienceStateUpdater
} from "src/recoil/atoms/trulience"
import { showDevicePermissionOverlayAtom } from "src/recoil/atoms/settings"
import { SDKContext } from "src/context/SDKContext"
import useOverlayControl from "src/hooks/useOverlayControl"
import { InspirationModel } from "src/components/views/avatars/components/Inspirations"

import useCurrentAvatarInfo from "./useCurrentAvatarInfo"
import {
  avatarCommunication,
  AvatarEvent,
  AvatarMessageType
} from "../AvatarCommunication"
import { queuedInspirationAtom } from "src/recoil/atoms/inspirations"
import { useHistory } from "react-router-dom"
import useConfig from "src/hooks/useConfig"
import { useHandleAttachmentUpload } from "src/hooks/useHandleAttachmentUpload"
import { useWatchPartyController } from "src/hooks/useWatchParty"
import {
  LearnMachineOnboardingData,
  useOnboarding
} from "src/hooks/useOnboarding"
import { delay } from "src/shared/utils"

const logger = new ConsoleLogger()

type micAccessHandlerResponseType =
  | boolean
  | { permissionGranted: boolean; state: "granted" | "prompt" | "denied" }

const ignoreMessages = [
  "<trl-state enableListening='true' />",
  "<trl-state enableListening='false' />",
  "<trl-info processingInput='true' />",
  "<trl-content orientation='portrait' />",
  "<trl-content orientation='landscape' />",
  "<trl-enable-joystick />",
  "<trl-disable-joystick />"
]

const useTrulienceAvatarEventListener = (
  avatarInitialised: boolean,
  {
    queuedInspiration,
    authedUser
  }: { queuedInspiration: InspirationModel; authedUser: AuthedUser }
) => {
  // Try to use only setter state
  const avatarPhotoRef = useRef(null)
  const avatarConfig = useCurrentAvatarInfo()
  const meetingClient = useContext(SDKContext)
  const setChatIsRead = useSetRecoilState(chatIsReadSelector)
  const { allowAllOverlays, hideAllOverlays } = useOverlayControl()
  const updateTrulienceState = useSetRecoilState(TrulienceStateUpdater)
  const trulienceStateCallback = useRecoilValue(TrulienceStateCallbackSelector)
  const setShowDevicePermission = useSetRecoilState(
    showDevicePermissionOverlayAtom
  )
  const { getOnboardingPromise } = useOnboarding()
  const setQueuedInspiration = useSetRecoilState(queuedInspirationAtom)
  const history = useHistory()
  const config = useConfig()
  const generation = useRecoilValue(currentChatGenerationAtom)
  const { triggerWatchPartyOnUpload } = useHandleAttachmentUpload()

  /** Auth Handlers */
  const handleOnAuthSuccess = (eventParams: { avatarPhotoURL: string }) => {
    // Set the avatar photo url
    if (eventParams.avatarPhotoURL) {
      avatarPhotoRef.current = eventParams.avatarPhotoURL
      updateTrulienceState({ avatarPhotoUrl: eventParams.avatarPhotoURL })
    }
  }

  /** Websocket Handlers */
  const handleWebsocketConnect = async () => {
    await delay(1000)
    const onboardings = await getOnboardingPromise()
    let message = ""
    // send wake msg

    let additionalInstruction =
      "These are facts that you know about the user but do not need to mention unless asked for or found to be relevant to the current conversation. Greet the user with a random greeting, do not mention the persona."
    if (
      config.platformName === "LearnMachine" &&
      onboardings &&
      onboardings?.length > 0
    ) {
      const onboarding = onboardings?.at(0)?.data as
        | LearnMachineOnboardingData
        | undefined
      if (onboarding) {
        additionalInstruction += ` The user is a GCSE ${onboarding.selectedCourse} student for the ${onboarding.selectedBoard} exam board on the ${onboarding.selectedLevel} level. Don't talk about it unless asked, but do adjust your answers accordingly to the user's level (Foundation students may need a bit more help with easier problems, Intermediate students slightly less so and Higher level students will be attempting the hardest of problems).`
      }
    }
    if (!authedUser.screenName) {
      avatarCommunication.sendMessage(t("Hi"))
      message += t("Hi") + "\n"
    } else if (authedUser.screenName && !authedUser.personaDescription) {
      message += `${t("This user's name is")} ${
        authedUser.screenName
      }. ${additionalInstruction}\n`
    } else {
      message += `${t("This user's name is")}. ${authedUser.screenName} ${
        !!authedUser.personaDescription
          ? `. ${t("Their persona can be described as:")} ${
              authedUser.personaDescription
            }`
          : ""
      }. ${additionalInstruction}\n`
    }
    if (!!queuedInspiration) {
      meetingClient?.sendPublicChat(
        !!queuedInspiration.chatContent
          ? queuedInspiration.chatContent
          : queuedInspiration.title,
        undefined,
        null,
        undefined,
        avatarConfig?.id?.toString(),
        generation
      )
      switch (config.platformName) {
        case "LearnMachine": {
          message += `<lm-load-topic topic="${queuedInspiration.tag}" />`
          break
        }
        default: {
          message += queuedInspiration.prompt
          break
        }
      }
      setQueuedInspiration(null)
    }
    avatarCommunication.sendMessage(message)
  }

  const handleWebsocketClose = () => {
    history.replace(config.postLoginRedirectUrl)
  }
  const [uploadedAttachmentUrl, setUploadedAttachmentUrl] = useRecoilState(
    recentUploadAttachmentUrlAtom
  )

  const handleWebsocketMessage = eventParams => {
    logger.log("Websocket message:", eventParams)

    const extractQuestionImageUrl = (message: string): string | null => {
      // Update to accommodate spaces around and within attributes and URL path
      const htmlEntityPattern =
        /&lt;trl-lm-question-loaded\s+[^&]*questionImageUrl=(['"]?)(https:\/\/[^'"]+)\1\/&gt;/i
      const standardTagPattern =
        /<trl-lm-question-loaded\s+[^>]*questionImageUrl=(['"]?)(https:\/\/[^'"]+)\1\/>/i

      let match = message.match(htmlEntityPattern)
      if (!match) {
        match = message.match(standardTagPattern)
      }

      return match ? match[2] : null
    }

    const isQuestionLoadedMessage = (message: string): boolean => {
      // Regex pattern for HTML-encoded self-closing tag
      const htmlEntityPattern = /&lt;trl-lm-question-loaded\s+.*?\/&gt;/i

      // Regex pattern for standard self-closing HTML tag
      const standardTagPattern = /<trl-lm-question-loaded\s+.*?\/>/i

      // Check if the message matches either of the patterns
      return htmlEntityPattern.test(message) || standardTagPattern.test(message)
    }

    if (eventParams.messageType == AvatarMessageType.ChatText) {
      // if (typeof eventData.message == "string" && (eventData.message as string).startsWith("<trl")) return
      let message = eventParams.messageArray[0].message

      if (ignoreMessages.includes(message)) return
      // remove <trl-content queue='true' /> and the blank space that comes after it
      message = message.replace(/^<trl-[^>]*\/>\s*/, "").trim()
      if (message === "") return
      // Check if it is a <trl-lm-question-loaded> message
      if (isQuestionLoadedMessage(message)) {
        logger.log("is question loaded message")
        const questionImageUrl = extractQuestionImageUrl(message)
        if (questionImageUrl) {
          logger.log("Extracted questionImageUrl:", questionImageUrl)

          meetingClient?.sendPublicChatAttachmentAsAvatar(
            "",
            questionImageUrl,
            generation,
            avatarConfig?.id?.toString(),
            avatarConfig.avatarName,
            avatarPhotoRef.current
          )
        }

        if (config.showAttachmentInMvwOnSend) {
          triggerWatchPartyOnUpload()
          setUploadedAttachmentUrl(questionImageUrl)
        }
        return
      }

      const senderType = eventParams.senderType // TODO: handle DH's new senderType once its fixed
      if (senderType === "AVATAR" || senderType !== "USER") {
        meetingClient?.sendPublicChatAsAvatar(
          message,
          avatarConfig?.id?.toString(),
          avatarConfig.avatarName,
          avatarPhotoRef.current,
          generation
        )
      } else {
        // Handle user voice response
        meetingClient?.sendPublicChat(
          message,
          null,
          null,
          null,
          avatarConfig?.id?.toString(),
          generation
        )
      }
      setChatIsRead(true)
    }
  }

  /** Media Handlers */
  const handleMediaConnected = async () => {
    console.log("In callback mediaConnectedHandler")
    // Enable mic and speaker if you want to enable it by default
    avatarCommunication.fixAudioContext()

    const trulienceState = await trulienceStateCallback.getTrulienceState()
    avatarCommunication.setMicStatus(trulienceState.micEnabled)
    avatarCommunication.setSpeakerStatus(trulienceState.speakerEnabled)
  }

  const handleMicAccess = (resp: micAccessHandlerResponseType) => {
    logger.log("In callback micAccessHandler resp = ", resp)
    if (typeof resp === "boolean") {
      updateTrulienceState({ micEnabled: resp })
      return
    }

    updateTrulienceState({
      micEnabled: resp.permissionGranted,
      micPermissionState: resp.state
    })

    if (resp.state === "denied") {
      const isMobileApp = useIsMobileApp()

      if (isMobileApp) {
        meetingClient?.nativeBridge.eventMicAccessPermissionRequired()
      } else {
        setShowDevicePermission({
          state: "denied",
          type: "microphone"
        })
      }
    }
  }

  const handleMicStatus = (status: boolean) => {
    avatarCommunication.fixAudioContext()
    updateTrulienceState({ micEnabled: status })
  }

  const handleSpeakerStatus = (status: boolean) => {
    avatarCommunication.fixAudioContext()
    updateTrulienceState({ speakerEnabled: status })
  }

  /** Avatar Event Handlers */
  const handleAvatarLoadProgress = (resp: { percent: number }) => {
    if (resp.percent === 1) {
      logger.log("Avatar has been successfully loaded ", resp.percent)
      meetingClient?.nativeBridge?.eventAvatarLoaded()
    }
  }

  useEffect(() => {
    if (!avatarInitialised) return
    const iframeElement = document.getElementById(
      "trl-iframe"
    ) as HTMLIFrameElement
    avatarCommunication.init(iframeElement)
    updateTrulienceState({ iframeElement })

    avatarCommunication.on(AvatarEvent.WebsocketConnect, handleWebsocketConnect)
    avatarCommunication.on(AvatarEvent.WebsocketClose, handleWebsocketClose)
    avatarCommunication.on(AvatarEvent.MicUpdate, handleMicStatus)
    avatarCommunication.on(AvatarEvent.SpeakerUpdate, handleSpeakerStatus)
    avatarCommunication.on(AvatarEvent.AuthSuccess, handleOnAuthSuccess)
    avatarCommunication.on(AvatarEvent.WebsocketMessage, handleWebsocketMessage)
    avatarCommunication.on(AvatarEvent.MediaConnected, handleMediaConnected)
    avatarCommunication.on(AvatarEvent.MicAccess, handleMicAccess)
    avatarCommunication.on(AvatarEvent.ModalDialogOpen, hideAllOverlays)
    avatarCommunication.on(AvatarEvent.ModalDialogClose, allowAllOverlays)
    avatarCommunication.on(
      AvatarEvent.AvatarLoadProgress,
      handleAvatarLoadProgress
    )

    return () => {
      avatarCommunication.off(
        AvatarEvent.WebsocketConnect,
        handleWebsocketConnect
      )
      avatarCommunication.off(AvatarEvent.MicUpdate, handleMicStatus)
      avatarCommunication.off(AvatarEvent.SpeakerUpdate, handleSpeakerStatus)
      avatarCommunication.off(AvatarEvent.AuthSuccess, handleOnAuthSuccess)
      avatarCommunication.off(
        AvatarEvent.WebsocketMessage,
        handleWebsocketMessage
      )
      avatarCommunication.off(AvatarEvent.MicAccess, handleMicAccess)
      avatarCommunication.off(AvatarEvent.ModalDialogOpen, hideAllOverlays)
      avatarCommunication.off(AvatarEvent.ModalDialogClose, allowAllOverlays)
      avatarCommunication.off(AvatarEvent.MediaConnected, handleMediaConnected)
      avatarCommunication.off(
        AvatarEvent.AvatarLoadProgress,
        handleAvatarLoadProgress
      )
      avatarCommunication.reset()
    }
  }, [avatarInitialised])
}

export default useTrulienceAvatarEventListener
